/**
 * Pure logic for the "yes, it's me" self-registration path of the underage
 * invite flow — the counterpart to buildPlayerPayload, which serves the
 * parent/guardian path.
 *
 * Context: an invite can be flagged `show_underage_workflow` while carrying no
 * `birth_date`. In that case the backend does not actually know the invitee is
 * a minor — it only knows the DOB is missing. When such an invitee answers
 * "yes, it's me" on the Welcome step we must not dead-end them on the
 * UnderAgeError screen; they get the same register form as a regular invite
 * and supply their own DOB.
 *
 * React-agnostic on purpose: the request bodies and the pre-flight validation
 * are the parts worth testing, and keeping them here lets both the view and
 * the tests share one definition.
 */

import { SELF_RELATIONSHIP_TYPE_ID } from '../../profile/family/constants'

export interface SelfRegisterFormValues {
  nameFirst: string
  nameLast: string
  password: string
  confirmPassword: string
  /** ISO datetime from the shared DOB component, or '' / false when incomplete. */
  birthDate: string | false
}

/**
 * Player payload attached to the self-register signup. The invitee is claiming
 * their own player record, so the relationship is always Self.
 *
 * height/weight are not collected on this form — the BE is sent the same
 * "empty" values the guardian flow produces when those inputs are untouched.
 */
export interface SelfRegisterPlayerPayload {
  name_first: string
  name_last: string
  birth_date: string | null
  height: null
  weight: string
  id: string
  relationship_type_id: number
}

export interface SelfRegisterRequestBody {
  email: string
  invitation_id: string
  name_first: string
  name_last: string
  password: string
  confirm_password: string
  birth_date?: string
  player: SelfRegisterPlayerPayload | null
}

/** Field-level keys the register form renders errors against. */
export type SelfRegisterErrorField =
  | 'name_first'
  | 'name_last'
  | 'password'
  | 'confirm_password'
  | 'birth_date'

export type SelfRegisterErrors = Partial<Record<SelfRegisterErrorField, string>>

/**
 * The invite fields this path cares about. Kept structural rather than
 * importing the page's ResponseData so common/ stays independent of web/.
 */
export interface SelfRegisterInvite {
  email?: string | null
  birth_date?: string | null
  /** Player record the invitee is claiming; becomes the player payload's id. */
  player_id?: string | null
}

/**
 * Whether the "yes, it's me" answer should open the self-register form rather
 * than the UnderAgeError dead end.
 *
 * Only when the invite has NO birth_date: a flagged invite that *does* carry a
 * DOB describes a genuine minor, and those must still be routed to a
 * parent/guardian.
 */
export const canSelfRegister = (invite?: SelfRegisterInvite | null): boolean =>
  !invite?.birth_date

/** Strips the time component from the DOB component's ISO datetime. */
export const toBirthDateOnly = (birthDate: string | false): string | null => {
  if (!birthDate) {
    return null
  }
  const [datePart] = birthDate.split('T')
  return datePart || null
}

/**
 * Client-side gate mirroring the regular register form: DOB is mandatory on
 * this path (the whole reason we are here is that it is missing), and the two
 * password fields must agree before we spend a round trip.
 */
export const validateSelfRegister = (
  values: SelfRegisterFormValues
): SelfRegisterErrors => {
  const errors: SelfRegisterErrors = {}

  if (!values.nameFirst?.trim()) {
    errors.name_first = 'First name is required'
  }
  if (!values.nameLast?.trim()) {
    errors.name_last = 'Last name is required'
  }
  if (!values.password) {
    errors.password = 'Password is required'
  }
  if (!toBirthDateOnly(values.birthDate)) {
    errors.birth_date = 'Date of birth is required'
  }
  if (values.password && values.password !== values.confirmPassword) {
    errors.confirm_password = 'Passwords do not match'
  }

  return errors
}

export const hasErrors = (errors: SelfRegisterErrors): boolean =>
  Object.keys(errors).length > 0

/**
 * Player payload for the self ("it's me") path, built from already-normalized
 * values.
 *
 * The invitee is the player, so name and DOB mirror their own account details
 * and the relationship is Self. Returns null when there is no player_id — no
 * record to claim, and the BE rejects a player without an id.
 *
 * Mobile's register screens keep name and DOB in their own context state and
 * normalize the date themselves, so they call this directly; web goes through
 * the form-values wrapper below.
 */
export const buildSelfPlayerPayloadFromValues = ({
  nameFirst,
  nameLast,
  birthDate,
  playerId,
}: {
  nameFirst: string
  nameLast: string
  /** Already 'YYYY-MM-DD' (or null when unset). */
  birthDate: string | null
  playerId?: string | null
}): SelfRegisterPlayerPayload | null => {
  if (!playerId) {
    return null
  }

  return {
    name_first: nameFirst,
    name_last: nameLast,
    birth_date: birthDate,
    height: null,
    weight: '',
    id: playerId,
    relationship_type_id: SELF_RELATIONSHIP_TYPE_ID,
  }
}

/** Form-values variant of buildSelfPlayerPayloadFromValues, used by web. */
export const buildSelfPlayerPayload = (
  values: SelfRegisterFormValues,
  invite: SelfRegisterInvite | null | undefined
): SelfRegisterPlayerPayload | null =>
  buildSelfPlayerPayloadFromValues({
    nameFirst: values.nameFirst,
    nameLast: values.nameLast,
    birthDate: toBirthDateOnly(values.birthDate),
    playerId: invite?.player_id,
  })

/**
 * Body for POST /signup. Carries the player payload so the new account is
 * linked to the player record the invite points at.
 */
export const buildSelfRegisterBody = (
  values: SelfRegisterFormValues,
  invite: SelfRegisterInvite | null | undefined,
  inviteId: string
): SelfRegisterRequestBody => {
  const body: SelfRegisterRequestBody = {
    email: invite?.email ?? '',
    invitation_id: inviteId,
    name_first: values.nameFirst,
    name_last: values.nameLast,
    password: values.password,
    confirm_password: values.confirmPassword,
    player: buildSelfPlayerPayload(values, invite),
  }

  const birthDate = toBirthDateOnly(values.birthDate)
  if (birthDate) {
    body.birth_date = birthDate
  }

  return body
}

/**
 * Body for POST /signup/signup-validate-mobile. Same shape as the signup body
 * minus `invitation_id` — including the player payload, so validation covers
 * the same fields the signup call will send.
 */
export const buildSelfRegisterValidateBody = (
  values: SelfRegisterFormValues,
  invite: SelfRegisterInvite | null | undefined,
  inviteId: string
): Omit<SelfRegisterRequestBody, 'invitation_id'> => {
  const body = buildSelfRegisterBody(values, invite, inviteId)
  delete (body as Partial<SelfRegisterRequestBody>).invitation_id
  return body
}

/**
 * Maps an API `invalid_fields` bag onto our field-error keys, ignoring any
 * key the form has nowhere to render.
 */
export const mapInvalidFields = (
  invalidFields?: Record<string, string> | null
): SelfRegisterErrors => {
  if (!invalidFields) {
    return {}
  }

  const fields: SelfRegisterErrorField[] = [
    'name_first',
    'name_last',
    'password',
    'confirm_password',
    'birth_date',
  ]

  return fields.reduce<SelfRegisterErrors>((acc, field) => {
    if (invalidFields[field]) {
      acc[field] = invalidFields[field]
    }
    return acc
  }, {})
}
